import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Navigation } from "./Dashboard";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useAuth, useTheme } from "../App";
import { toast } from "sonner";
import { 
  Trophy, 
  Users,
  Plus,
  Send,
  Clock,
  CheckCircle,
  XCircle,
  Moon,
  Sun,
  User,
  Home
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Community() {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [leaderboard, setLeaderboard] = useState([]);
  const [contributions, setContributions] = useState([]);
  const [myContributions, setMyContributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [newWord, setNewWord] = useState({
    baoulé: "",
    français: "",
    phonétique: "",
    catégorie: "général",
    exemple_baoulé: "",
    exemple_français: ""
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch leaderboard
        const leaderRes = await fetch(`${API}/leaderboard?limit=20`);
        if (leaderRes.ok) {
          const leaderData = await leaderRes.json();
          setLeaderboard(leaderData);
        }

        // Fetch contributions
        const contribRes = await fetch(`${API}/contributions?status=pending`);
        if (contribRes.ok) {
          const contribData = await contribRes.json();
          setContributions(contribData);
        }

        // Fetch my contributions if logged in
        if (user) {
          const token = localStorage.getItem("token");
          const myContribRes = await fetch(`${API}/contributions/my`, {
            headers: { "Authorization": `Bearer ${token}` }
          });
          if (myContribRes.ok) {
            const myContribData = await myContribRes.json();
            setMyContributions(myContribData);
          }
        }
      } catch (error) {
        console.error("Error fetching community data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const handleSubmitWord = async (e) => {
    e.preventDefault();
    
    if (!newWord.baoulé.trim() || !newWord.français.trim()) {
      toast.error("Veuillez remplir les champs obligatoires");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/contributions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(newWord)
      });

      if (response.ok) {
        toast.success("Contribution soumise ! +5 points");
        setNewWord({
          baoulé: "",
          français: "",
          phonétique: "",
          catégorie: "général",
          exemple_baoulé: "",
          exemple_français: ""
        });
        // Refresh my contributions
        const myContribRes = await fetch(`${API}/contributions/my`, {
          headers: { "Authorization": `Bearer ${token}` }
        });
        if (myContribRes.ok) {
          const myContribData = await myContribRes.json();
          setMyContributions(myContribData);
        }
      } else {
        throw new Error("Erreur lors de la soumission");
      }
    } catch (error) {
      console.error("Error submitting contribution:", error);
      toast.error("Erreur lors de la soumission");
    } finally {
      setSubmitting(false);
    }
  };

  const getRankColor = (index) => {
    if (index === 0) return "bg-yellow-500/20 text-yellow-600 border-yellow-500/30";
    if (index === 1) return "bg-gray-300/20 text-gray-500 border-gray-300/30";
    if (index === 2) return "bg-orange-400/20 text-orange-500 border-orange-400/30";
    return "bg-muted text-muted-foreground";
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <span className="px-2 py-1 rounded-full bg-yellow-500/10 text-yellow-600 text-xs flex items-center gap-1"><Clock className="w-3 h-3" /> En attente</span>;
      case "approved":
        return <span className="px-2 py-1 rounded-full bg-accent/10 text-accent text-xs flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Approuvé</span>;
      case "rejected":
        return <span className="px-2 py-1 rounded-full bg-destructive/10 text-destructive text-xs flex items-center gap-1"><XCircle className="w-3 h-3" /> Rejeté</span>;
      default:
        return null;
    }
  };

  // Non-authenticated view
  if (!user) {
    return (
      <div className="min-h-screen bg-background" data-testid="community-page">
        <nav className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                  <span className="text-primary-foreground font-heading font-bold text-xl">B</span>
                </div>
                <span className="font-heading font-bold text-xl hidden sm:block">Apprends Baoulé</span>
              </Link>

              <div className="flex items-center gap-3">
                <button
                  onClick={toggleTheme}
                  className="p-2 rounded-lg hover:bg-muted transition-colors"
                  aria-label="Toggle theme"
                >
                  {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </button>
                <Link to="/login">
                  <Button variant="outline" size="sm">Connexion</Button>
                </Link>
              </div>
            </div>
          </div>
        </nav>

        <main className="pt-20 pb-12 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="font-heading text-3xl font-bold mb-2">Communauté</h1>
              <p className="text-muted-foreground">
                Découvrez le classement et les contributions de la communauté
              </p>
            </div>

            {/* Leaderboard */}
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-primary" />
                  Classement
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {loading ? (
                    Array(5).fill(0).map((_, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 animate-pulse">
                        <div className="w-8 h-8 rounded-full bg-muted"></div>
                        <div className="w-10 h-10 rounded-full bg-muted"></div>
                        <div className="flex-1 h-4 bg-muted rounded"></div>
                        <div className="w-16 h-4 bg-muted rounded"></div>
                      </div>
                    ))
                  ) : (
                    leaderboard.map((entry, index) => (
                      <div
                        key={entry.user_id}
                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 ${getRankColor(index)}`}>
                          {index + 1}
                        </span>
                        {entry.picture ? (
                          <img src={entry.picture} alt={entry.name} className="w-10 h-10 rounded-full" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                            <User className="w-5 h-5" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{entry.name}</p>
                          <p className="text-sm text-muted-foreground capitalize">{entry.level}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-primary">{entry.points} pts</p>
                          <p className="text-xs text-muted-foreground">{entry.streak} jours</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="mt-6 p-4 rounded-xl bg-primary/5 text-center">
                  <p className="text-sm text-muted-foreground mb-3">
                    Connectez-vous pour contribuer et apparaître au classement !
                  </p>
                  <Link to="/register">
                    <Button className="rounded-full">Créer un compte</Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  // Authenticated view
  return (
    <div className="min-h-screen bg-background" data-testid="community-page">
      <Navigation />
      
      <main className="pt-20 pb-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8 animate-slide-up">
            <h1 className="font-heading text-3xl font-bold mb-2">Communauté</h1>
            <p className="text-muted-foreground">
              Contribuez au dictionnaire et grimpez dans le classement !
            </p>
          </div>

          <Tabs defaultValue="leaderboard" className="animate-fade-in">
            <TabsList className="mb-6">
              <TabsTrigger value="leaderboard" className="flex items-center gap-2">
                <Trophy className="w-4 h-4" />
                Classement
              </TabsTrigger>
              <TabsTrigger value="contribute" className="flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Contribuer
              </TabsTrigger>
              <TabsTrigger value="my-contributions" className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Mes contributions
              </TabsTrigger>
            </TabsList>

            {/* Leaderboard Tab */}
            <TabsContent value="leaderboard">
              <Card className="border-border/50">
                <CardContent className="p-6">
                  <div className="space-y-3">
                    {loading ? (
                      Array(10).fill(0).map((_, i) => (
                        <div key={i} className="flex items-center gap-3 p-3 animate-pulse">
                          <div className="w-8 h-8 rounded-full bg-muted"></div>
                          <div className="w-10 h-10 rounded-full bg-muted"></div>
                          <div className="flex-1 h-4 bg-muted rounded"></div>
                          <div className="w-16 h-4 bg-muted rounded"></div>
                        </div>
                      ))
                    ) : (
                      leaderboard.map((entry, index) => (
                        <div
                          key={entry.user_id}
                          className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                            entry.user_id === user?.user_id 
                              ? "bg-primary/5 border border-primary/20" 
                              : "hover:bg-muted/50"
                          }`}
                        >
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 ${getRankColor(index)}`}>
                            {index + 1}
                          </span>
                          {entry.picture ? (
                            <img src={entry.picture} alt={entry.name} className="w-10 h-10 rounded-full" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                              <User className="w-5 h-5" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">
                              {entry.name}
                              {entry.user_id === user?.user_id && (
                                <span className="ml-2 text-xs text-primary">(vous)</span>
                              )}
                            </p>
                            <p className="text-sm text-muted-foreground capitalize">{entry.level}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-primary">{entry.points} pts</p>
                            <p className="text-xs text-muted-foreground">{entry.streak} jours</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Contribute Tab */}
            <TabsContent value="contribute">
              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle className="font-heading text-xl">Ajouter un mot</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Partagez vos connaissances et enrichissez le dictionnaire. Gagnez 5 points par contribution !
                  </p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmitWord} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="baoule">Mot en Baoulé *</Label>
                        <Input
                          id="baoule"
                          value={newWord.baoulé}
                          onChange={(e) => setNewWord({...newWord, baoulé: e.target.value})}
                          placeholder="Ex: N'goran"
                          required
                          data-testid="baoule-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="francais">Traduction en Français *</Label>
                        <Input
                          id="francais"
                          value={newWord.français}
                          onChange={(e) => setNewWord({...newWord, français: e.target.value})}
                          placeholder="Ex: Bonjour"
                          required
                          data-testid="francais-input"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phonetique">Phonétique</Label>
                        <Input
                          id="phonetique"
                          value={newWord.phonétique}
                          onChange={(e) => setNewWord({...newWord, phonétique: e.target.value})}
                          placeholder="Ex: n-go-ran"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="categorie">Catégorie</Label>
                        <Select
                          value={newWord.catégorie}
                          onValueChange={(value) => setNewWord({...newWord, catégorie: value})}
                        >
                          <SelectTrigger data-testid="category-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="général">Général</SelectItem>
                            <SelectItem value="salutations">Salutations</SelectItem>
                            <SelectItem value="nombres">Nombres</SelectItem>
                            <SelectItem value="famille">Famille</SelectItem>
                            <SelectItem value="nourriture">Nourriture</SelectItem>
                            <SelectItem value="nature">Nature</SelectItem>
                            <SelectItem value="verbes">Verbes</SelectItem>
                            <SelectItem value="expressions">Expressions</SelectItem>
                            <SelectItem value="proverbes">Proverbes</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="exemple_baoule">Exemple en Baoulé</Label>
                      <Textarea
                        id="exemple_baoule"
                        value={newWord.exemple_baoulé}
                        onChange={(e) => setNewWord({...newWord, exemple_baoulé: e.target.value})}
                        placeholder="Une phrase d'exemple utilisant ce mot..."
                        rows={2}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="exemple_francais">Traduction de l'exemple</Label>
                      <Textarea
                        id="exemple_francais"
                        value={newWord.exemple_français}
                        onChange={(e) => setNewWord({...newWord, exemple_français: e.target.value})}
                        placeholder="Traduction française de l'exemple..."
                        rows={2}
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="rounded-full"
                      disabled={submitting}
                      data-testid="submit-contribution-btn"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {submitting ? "Envoi..." : "Soumettre"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            {/* My Contributions Tab */}
            <TabsContent value="my-contributions">
              <Card className="border-border/50">
                <CardContent className="p-6">
                  {myContributions.length === 0 ? (
                    <div className="text-center py-12">
                      <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <h3 className="font-heading text-lg font-semibold mb-2">Aucune contribution</h3>
                      <p className="text-muted-foreground mb-4">
                        Vous n'avez pas encore contribué au dictionnaire.
                      </p>
                      <Button 
                        variant="outline"
                        onClick={() => document.querySelector('[value="contribute"]')?.click()}
                      >
                        Faire ma première contribution
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {myContributions.map((contrib) => (
                        <div
                          key={contrib.contribution_id}
                          className="p-4 rounded-xl border border-border"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h4 className="font-heading font-semibold">{contrib.baoulé}</h4>
                              <p className="text-muted-foreground">{contrib.français}</p>
                            </div>
                            {getStatusBadge(contrib.status)}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span className="px-2 py-0.5 rounded-full bg-muted">{contrib.catégorie}</span>
                            <span>
                              {new Date(contrib.created_at).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
